import { h, FunctionComponent } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import { Trans, useTranslation } from 'react-i18next';
import { batch, useDispatch, useSelector } from 'react-redux';
import * as styles from '~src/styles/domains/loanConsent/LoanConsentPage.module.scss';
import {
  generateDoc,
  getExternalDocumentOrder,
  loadDocument,
  translateDocument,
} from '~src/utils/documents/documents';
import { setScrollListeners } from '~src/utils/documents/scroll';
import { trackPageMount } from '~src/utils/tracking';
import { xhrNext } from '~src/utils/xhr';
import Alerter from '../common/components/Alerter';
import SimpleButton from '../common/components/Button';
import DocumentLink from '../common/components/DocumentLink';
import { Page } from '../common/enums/api';
import { AlertNames, PaymentOption } from '../common/enums/common';
import { Doc } from '../common/enums/docs';
import { Language } from '../common/enums/localization';
import {
  general,
  setDocument,
  setDocumentRef,
} from '../common/state/commonReducer';
import {
  brandSelector,
  commonDocsSelector,
  documentSelector,
  isMultipleOfferFlowSelector,
  lazyDocsInFlightSelector,
  lazyDocsSelector,
  loanOfferSelectionSelector,
  loanOffersSelector,
  localeSelector,
  mcodeSelector,
  paymentMethodSelector,
  paymentOptionsSelector,
  sessionAutoPaySelector,
} from '../common/state/commonSelectors';
import { SessionType } from '../common/types/api';
import DocumentJumpLink from './components/DocumentJumpLink';
import { setDocOrder } from './state/loanConsentReducer';
import { docOrderSelector } from './state/loanConsentSelectors';
import OpenInNewTabLink from './components/OpenInNewTabLink';

const getDocOrder = (
  commonDocs: SessionType['common_docs'],
  baseDocOrder: Doc[],
): Doc[] | [] => {
  const isAchEnabled = commonDocs?.hasOwnProperty(Doc.ACH);
  const isDcdEnabled = commonDocs?.hasOwnProperty(Doc.DCD);

  const docOrder = [...baseDocOrder];

  if (isAchEnabled) {
    docOrder.push(Doc.ACH);
  }
  if (isDcdEnabled) {
    docOrder.push(Doc.DCD);
  }
  if (commonDocs?.hasOwnProperty(Doc.ICC)) {
    docOrder.push(Doc.ICC);
  }

  if (commonDocs?.hasOwnProperty(Doc.DRD)) {
    docOrder.push(Doc.DRD);
  }

  return docOrder;
};

const LoanConsentPage: FunctionComponent = () => {
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();

  const [wiNoticeOpen, setWiNoticeOpen] = useState<boolean>(false);
  const toggleSection = () => {
    setWiNoticeOpen(!wiNoticeOpen);
  };

  const docRef = useRef(null);
  const [externalDocOrder, setExternalDocOrder] = useState<Doc[]>([]);

  const brand = useSelector(brandSelector);
  const currDoc = useSelector(documentSelector);
  const currLocale = useSelector(localeSelector);
  const docOrder = useSelector(docOrderSelector);
  const lazyDocs = useSelector(lazyDocsSelector);
  const locale = useSelector(localeSelector);
  const isMultipleOfferFlow = useSelector(isMultipleOfferFlowSelector);
  const loanOfferSelectionId = useSelector(loanOfferSelectionSelector);
  const sessionAutoPay = useSelector(sessionAutoPaySelector);
  const loanOffers = useSelector(loanOffersSelector);
  const paymentOptions = useSelector(paymentOptionsSelector);
  const selectedPaymentMethod = useSelector(paymentMethodSelector);
  const mcode = useSelector(mcodeSelector);
  const lazyDocsInFlight = useSelector(lazyDocsInFlightSelector);
  const isAutoPayEnabled = !!sessionAutoPay;
  const loanOfferSelection = loanOffers.find(
    offer => offer.offer_id === loanOfferSelectionId,
  );
  const commonDocs = useSelector(
    commonDocsSelector,
  ) as SessionType['common_docs'];
  const baseDocOrder: Doc[] = t(`loanConsent:documentOrder`, {
    returnObjects: true,
  });

  const isAchEnabled = commonDocs?.hasOwnProperty(Doc.ACH);
  const isDcdEnabled = commonDocs?.hasOwnProperty(Doc.DCD);
  const isWisconsinEnabled = commonDocs?.hasOwnProperty(Doc.WD);
  const achDocName = t('loanConsent:docName.ach');
  const dcdDocName = t('loanConsent:docName.dcd');
  const submitButtonLabel = t('loanConsent:submit');
  const [language, _] = locale.split('-');
  const isCreditEnabled = paymentOptions.find(
    (option: PaymentOption) => option === PaymentOption.CREDIT,
  );

  // Set the correct document order on page mount
  useEffect(() => {
    if (!baseDocOrder) {
      return;
    }
    const newDocOrder = getDocOrder(commonDocs, baseDocOrder);
    const extDocOrder = getExternalDocumentOrder(commonDocs);
    batch(() => {
      dispatch(setDocOrder(newDocOrder));
      setExternalDocOrder(extDocOrder);
      dispatch(trackPageMount(Page.LOAN_CONSENT));
    });
  }, []);

  useEffect(() => {
    if (commonDocs && docOrder.length) {
      const firstDoc = docOrder.find((id: Doc) => !lazyDocs[id]);
      if (firstDoc) {
        const firstDocUrl = commonDocs[firstDoc]?.url;
        dispatch(loadDocument(firstDocUrl, firstDoc));
        setScrollListeners();
      } else {
        // If there are no docs that need to be loaded, just set document to the first lazy doc
        const firstLazyDoc = docOrder.find((id: Doc) => lazyDocs[id]);
        batch(() => {
          dispatch(
            general({
              lazyDocsInFlight: false,
            }),
          );
          dispatch(
            setDocument({
              id: firstLazyDoc,
              content: null,
              url: null,
              show: false,
              loading: false,
            }),
          );
        });
      }
    }
  }, [docOrder]);

  useEffect(() => {
    dispatch(setDocumentRef(docRef.current));

    return () => {
      dispatch(setDocumentRef(null));
    };
  }, [docRef]);

  // Translate document whenever locale changes or the document changes
  useEffect(() => {
    const docId = currDoc.id;
    if (docId) {
      dispatch(translateDocument(docId, currLocale, i18n));
    }
  }, [currDoc.id, currLocale]);

  const templateTermsCSD = () => {
    const title = t('loanConsent:termsPurchase.csd');
    const loanOfferCsd = loanOfferSelection?.docs?.csd;
    const url =
      isMultipleOfferFlow && loanOfferSelection && loanOfferCsd
        ? loanOfferCsd.url
        : loanOffers[0]?.docs?.csd?.url || '';

    return (
      <Trans
        i18nKey={'loanConsent:addCsdTerms'}
        t={t}
        components={{
          termsLink: <DocumentLink id={Doc.CSD} url={url} text={title} />,
          wisconsinLink: isWisconsinEnabled ? (
            <span data-auto='wisconsin-disclosure' />
          ) : (
            ''
          ),
        }}
      />
    );
  };

  // US only, therefore no localization on the components text
  const getAutomaticPayments = () => {
    if (isAchEnabled || isDcdEnabled) {
      return (
        <Trans
          i18nKey='loanConsent:addAutomaticPayments'
          t={t}
          components={{
            text: ', and the ',
            title: 'Recurring Electronic Payment Authorization. ',
          }}
        />
      );
    }
    return '. ';
  };

  return (
    <div className={styles['loan-consent-wrapper']}>
      <div className={styles['body-wrapper']}>
        {mcode != undefined && (
          <Alerter type={AlertNames.ERROR} i18nKey={`common:mcodes.${mcode}`} />
        )}
        <div className={styles['agreements-list']}>
          {docOrder.map((doc: Doc) => {
            const documentName = t(`common:documents.${doc}`);
            return (
              <DocumentJumpLink
                key={doc}
                name={documentName}
                docId={doc}
                isActive={currDoc.id === doc}
                language={language as Language}
              />
            );
          })}
          {externalDocOrder.map((doc: Doc) => {
            const documentName = t(`common:documents.${doc}`);
            return (
              <OpenInNewTabLink key={doc} name={documentName} docId={doc} />
            );
          })}
        </div>
        <div
          className={`document multi-document ${styles.document}`}
          ref={docRef}
        >
          {/** Adding bootstrap styles for document field only */}
          <link
            rel='stylesheet'
            href='https://maxcdn.bootstrapcdn.com/bootstrap/3.4.0/css/bootstrap.min.css'
          />
          {docOrder.map((doc: Doc) => {
            return <div key={doc}>{generateDoc(lazyDocs, doc, t)}</div>;
          })}
        </div>
        <div className={styles['loan-consent-footer-wrapper']}>
          <div className={styles['disclaimer-text']} tabIndex={0}>
            <Trans
              i18nKey={`loanConsent:termsPurchase.${brand.toLowerCase()}.terms`}
              t={t}
              values={{
                docName: isAchEnabled
                  ? achDocName
                  : isDcdEnabled
                  ? dcdDocName
                  : t('loanConsent:docName.default'),
                btnLabel: submitButtonLabel,
              }}
              components={{
                automaticPayments: getAutomaticPayments(),
                csdTerms: templateTermsCSD(),
              }}
            />
          </div>
          {commonDocs?.hasOwnProperty(Doc.WD) && (
            <div className={styles['wi-notice']}>
              <div
                className={styles['wi-notice-header']}
                onClick={toggleSection}
                id='wi-notice-toggle'
              >
                <span>{t('common:documents.wiNotice')}</span>

                <svg
                  className={!wiNoticeOpen ? styles['is-open'] : ''}
                  fill='#000000'
                  version='1.1'
                  viewBox='0 0 30.727 30.727'
                  width='20'
                  height='20'
                >
                  <g>
                    <path d='M29.994,10.183L15.363,24.812L0.733,10.184c-0.977-0.978-0.977-2.561,0-3.536c0.977-0.977,2.559-0.976,3.536,0   l11.095,11.093L26.461,6.647c0.977-0.976,2.559-0.976,3.535,0C30.971,7.624,30.971,9.206,29.994,10.183z' />
                  </g>
                </svg>
              </div>
              <div
                className={`${
                  wiNoticeOpen
                    ? styles['is-open-notice']
                    : styles['is-close-notice']
                }`}
                id='wi-notice-content'
              >
                <Trans>{t('common:documents.wiDisclaimer')}</Trans>
              </div>
            </div>
          )}
          <SimpleButton
            label={submitButtonLabel}
            trackingLabel={Page.LOAN_CONSENT}
            classLabel={`btn-primary ${styles['loan-consent-submit']}`}
            onClickPost={() => {
              dispatch(xhrNext());
            }}
            isDisabled={lazyDocsInFlight}
            trackClickPayload={
              isCreditEnabled || isAutoPayEnabled
                ? {
                    'payment-method': selectedPaymentMethod,
                    label: submitButtonLabel,
                    autopay: isAutoPayEnabled,
                  }
                : {}
            }
          />
        </div>
      </div>
    </div>
  );
};

export default LoanConsentPage;
